import { Suspense, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { Switch, useLocation } from 'react-router-dom';
import { SecureRoute } from '@okta/okta-react';
import classNames from 'classnames';

import { ConfirmPopup, NotificationPopup, SystemPitchForm } from 'components/Popups';
import { Loading } from 'components/UI';

import { useMemoSelector } from 'hooks';
import {
  getActiveRoute,
  getAllRoutes,
  getIsMarkersVisible,
  getMainConfirmPopup,
  getMainNotification,
  getSystemPitchForm,
  setActiveNavItemId,
} from 'store';
import { capitalizeOnlyFirstChar, getNormalizedRoute } from 'utils';

import styles from './Main.module.scss';

const Main = (): JSX.Element => {
  const location = useLocation();
  const dispatch = useDispatch();

  const { isVisibleMarkers, activeRoute, systemPitchForm, mainConfirmPopup, mainNotification, allRoutes } =
    useMemoSelector((state) => ({
      isVisibleMarkers: getIsMarkersVisible(state),
      activeRoute: getActiveRoute(state),
      systemPitchForm: getSystemPitchForm(state),
      mainConfirmPopup: getMainConfirmPopup(state),
      mainNotification: getMainNotification(state),
      allRoutes: getAllRoutes(state),
    }));

  useEffect(() => {
    const { id, name, pageHeaderName } = getNormalizedRoute(allRoutes, location.pathname);

    if (!name && !pageHeaderName) return;
    dispatch(setActiveNavItemId(id || 0));
  }, [allRoutes, dispatch, location.pathname]);

  const renderMain = useMemo(
    () => (
      <Suspense fallback={<div />}>
        <Helmet>
          <title>{`${capitalizeOnlyFirstChar(
            activeRoute?.name || activeRoute?.browserTabName || ''
          )} | WCM Arrow`}</title>
        </Helmet>
        <Switch>
          {allRoutes.map(({ exact, id, path, component }) => (
            <SecureRoute exact={exact} key={id} path={path} component={component} />
          ))}
        </Switch>
      </Suspense>
    ),
    [activeRoute?.browserTabName, activeRoute?.name, allRoutes]
  );

  return (
    <div
      className={classNames(styles.mainContainer, {
        [styles.mainContainerMarkersVisible]: isVisibleMarkers,
      })}
    >
      {renderMain}
      {systemPitchForm && <SystemPitchForm item={systemPitchForm} />}
      <ConfirmPopup {...mainConfirmPopup} />
      <NotificationPopup isOpen={!!mainNotification} text={mainNotification} />
      <Loading />
    </div>
  );
};

export default Main;
